import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bot,
  Cable,
  Cloud,
  Gauge,
  Globe2,
  MessageCircle,
  Shield,
  Smartphone,
  Target,
} from 'lucide-react';
import React from 'react';
import { useReveal } from '../utils/useReveal';
import SectionLayout from './SectionLayout';

const Services: React.FC = () => {
  const { ref, isVisible } = useReveal();
  const services = [
    {
      title: 'Customer Experience',
      description: 'Tingkatkan kepuasan pelanggan dengan solusi omnichannel yang terintegrasi. Kelola semua interaksi pelanggan dari berbagai channel dalam satu platform.',
      icons: [
        { icon: MessageCircle as LucideIcon, label: 'Multi Channel' },
        { icon: BarChart3 as LucideIcon, label: 'Analytics' },
        { icon: Bot as LucideIcon, label: 'Automation' },
        { icon: Gauge as LucideIcon, label: 'Performance' },
        { icon: Target as LucideIcon, label: 'Personalization' },
      ],
    },
    {
      title: 'Digital Solutions',
      description: 'Transformasi digital bisnis Anda dengan teknologi terkini. Dari integrasi sistem hingga pengembangan aplikasi custom sesuai kebutuhan.',
      icons: [
        { icon: Globe2 as LucideIcon, label: 'Web Development' },
        { icon: Smartphone as LucideIcon, label: 'Mobile App' },
        { icon: Cable as LucideIcon, label: 'API Integration' },
        { icon: Cloud as LucideIcon, label: 'Cloud Solutions' },
        { icon: Shield as LucideIcon, label: 'Security' },
      ],
    },
  ];

  return (
    <>
      <style>{`
        .services-section {
          opacity: 0;
          transform: translateY(50px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }

        .services-section.revealed {
          opacity: 1;
          transform: translateY(0);
        }

        .carousel {
          position: relative;
          border-radius: 0;
          border: none;
          background: transparent;
        }

        .carousel-track {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
          justify-items: center;
        }

        .carousel-slide {
          min-width: 0;
          padding: 40px;
          background: var(--card-2, rgba(255, 255, 255, 0.02));
          border: 1px solid var(--border, rgba(255, 255, 255, 0.08));
          border-radius: 24px;
          width: 100%;
          max-width: 700px;
          transition: all 0.3s ease;
        }

        .carousel-slide:hover {
          background: var(--card, rgba(255, 255, 255, 0.03));
          border-color: var(--border-strong, rgba(255, 255, 255, 0.12));
          transform: translateY(-4px);
          box-shadow: 0 12px 28px rgba(0,0,0,0.15);
        }

        .service-title {
          font-size: clamp(28px, 4vw, 48px);
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1.2;
          margin-bottom: 24px;
        }

        .service-description {
          font-size: 17px;
          line-height: 1.8;
          color: var(--muted-2, rgba(255, 255, 255, 0.6));
          margin-bottom: 36px;
          max-width: 900px;
        }

        .icon-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin-top: 16px;
        }

        .icon-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 12px;
          background: transparent;
          border: 1px solid rgba(10, 22, 40, 0.06);
        }

        .icon {
          width: 20px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #0a1628;
        }

        .icon svg {
          width: 18px;
          height: 18px;
        }

        @media (max-width: 1024px) {
          .carousel-track {
            grid-template-columns: 1fr;
          }
        }

      `}</style>
      <SectionLayout id="layanan" maxWidth="2xl">
        <section 
          className={`services-section ${isVisible ? 'revealed' : ''}`} 
          ref={ref as React.RefObject<HTMLElement>}
        >
        <div className="section-header">
          <h2 className="section-title">Layanan Kami</h2>
          <p className="section-description">
            Kami menyediakan solusi lengkap untuk transformasi digital bisnis Anda dengan layanan terdepan di industri.
          </p>
        </div>
        <div className="carousel" id="servicesCarousel">
          <div className="carousel-track">
            {services.map((service, idx) => (
              <div key={idx} className="carousel-slide">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                <div className="icon-list">
                  {service.icons.map((item, iconIdx) => {
                    const Icon = item.icon;
                    return (
                    <div key={iconIdx} className="icon-item">
                        <span className="icon">
                          <Icon />
                        </span>
                      <span>{item.label}</span>
                    </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        </section>
      </SectionLayout>
    </>
  );
};

export default Services;

